import { cn } from '@/lib/utils'
import { CategoriaEnum } from '@acme/auth'
import logoMobile from 'assets/logo.svg'
import textoLogo from 'assets/ponto-agil-texto.svg'
import { useBackendMenu } from 'hooks'
import { useAuth } from 'hooks/useAuth'
import React from 'react'
import { Link } from 'react-router-dom'
import { MenuResponsivo } from 'templates/Menu/MenuResponsivo'
import { CentralNotificacoes } from '../../../templates/Notificacoes/Central'
import { Button } from '../Button'
import { ToggleTheme } from '../ToggleTheme'
import ProfileMenu from './ProfileMenu'

export function NavbarContainer({ children }: { children: React.ReactNode }) {
    return (
        <div
            className={cn(
                'bg-primary dark:bg-card text-primary-foreground',
                'static top-0 z-(--z-cabecalho) flex h-16 w-full items-center justify-center shadow-sm',
            )}
        >
            <div
                className={cn(
                    '2xl:container w-screen mx-auto',
                    'px-5 2xl:px-2',
                    'min-h-0! flex items-center',
                )}
            >
                {children}
            </div>
        </div>
    )
}

export function LogoPontoAgil() {
    return (
        <Link
            className={cn(
                'font-bold uppercase text-2xl whitespace-nowrap gap-x-2 flex items-center',
                '[&_img]:max-w-full [&_img]:h-auto [&_img]:align-middle [&_img]:block',
                'mr-1 cursor-pointer',
            )}
            to="/"
        >
            <div className="size-8 overflow-hidden">
                <img
                    src={logoMobile}
                    alt="logo"
                    loading="lazy"
                    style={{
                        // white
                        filter: 'invert(88%) sepia(100%) saturate(0%) hue-rotate(35deg) brightness(106%) contrast(101%)',
                    }}
                />
            </div>
            <span className="w-26 hidden sm:flex">
                <img
                    src={textoLogo}
                    alt="PONTO ÁGIL"
                    loading="lazy"
                    style={{
                        // white
                        filter: 'invert(88%) sepia(100%) saturate(0%) hue-rotate(35deg) brightness(106%) contrast(101%)',
                    }}
                />
            </span>
            <span className="sr-only">Voltar ao início</span>
        </Link>
    )
}

export function AppNavbar() {
    const menu = useBackendMenu()
    const user = useAuth()
    const categoria = user?.decoded?.categoria
    const diasAvaliacao = Number(user?.decoded?.diasAvaliacao ?? null)

    if (!user)
        return (
            <NavbarContainer>
                <LogoPontoAgil />
                <div className="min-w-0 flex-1" />
                <ToggleTheme />
            </NavbarContainer>
        )

    if (!user.auth || location.pathname === '/ajuda/atendimento-online')
        return null

    return (
        <NavbarContainer>
            <LogoPontoAgil />
            {menu ? <MenuResponsivo itens={menu as any} /> : <></>}
            <div className="min-w-0 flex-1" />
            {(() => {
                if (categoria === CategoriaEnum.Avaliacao && diasAvaliacao > -1)
                    return (
                        <BoxAssinar href="/planos">
                            {(() => {
                                switch (diasAvaliacao) {
                                    case 0:
                                        return 'Avaliação: Termina hoje'
                                    case 1:
                                        return 'Avaliação: Um dia restante'
                                    default:
                                        return `Avaliação: ${diasAvaliacao} dias restantes`
                                }
                            })()}
                        </BoxAssinar>
                    )
                else if (
                    user.decoded.isAtivo === false &&
                    ![
                        CategoriaEnum.Administrador,
                        CategoriaEnum.Suporte,
                        CategoriaEnum.Representante,
                    ].includes(categoria)
                )
                    return (
                        <BoxAssinar
                            href="/planos/me"
                            className="sm:[&_span]:font-semibold"
                        >
                            Sua assinatura expirou!
                        </BoxAssinar>
                    )
            })()}
            <div className="flex flex-row items-center justify-center gap-0.5 pl-2">
                <CentralNotificacoes key={categoria} />
                <ToggleTheme />
                <ProfileMenu />
            </div>
        </NavbarContainer>
    )
}

function BoxAssinar({
    children,
    href,
    className,
}: {
    children: React.ReactNode
    href: string
    className?: string
}) {
    const { decoded } = useAuth()

    const podeRedirecionar = [
        CategoriaEnum.Cliente,
        CategoriaEnum.Avaliacao,
    ].includes(decoded?.categoria)

    return [
        <div
            key="desktop"
            className={cn(
                'hidden sm:flex shrink-0',
                'items-center justify-end',
                'gap-2 max-w-[600px] mr-1',
                className,
            )}
        >
            <span className="text-sm text-end break-words pl-2 pr-1 max-w-28">
                {children}
            </span>
            {podeRedirecionar && (
                <Button color="warning" href={href}>
                    Assinar
                </Button>
            )}
        </div>,
        <Button
            key="mobile"
            className={cn(
                'flex sm:hidden mr-1',
                'max-h-full p-0 px-2',
                'sm:p-2 text-center my-1',
                className,
            )}
            disabled={!podeRedirecionar}
            color="warning"
            href={href}
        >
            {children}
        </Button>,
    ]
}
